import { Box } from '@mui/material';
import OperationPreviewIndicator from '~/components/toolbar/OperationsToolbar/OperationPreviewIndicator';
import ToolTrayPresenceProvider from '~/components/toolbar/shared/ToolTrayPresenceProvider';
import ContouringStepTools from '~/components/toolbar/steps/ContouringTools';
import DefaultTools from '~/components/toolbar/steps/DefaultTools';
import DoseTools from '~/components/toolbar/steps/DoseTools';
import RegistrationTools from '~/components/toolbar/steps/RegistrationTools';
import useWorkflowSteps from '~/containers/viewer/explorer-sidebar/WorkflowStepper/useWorkflowSteps';

/** Tools of a workflow step; a step absent here gets {@link DefaultTools}. */
const STEP_TOOLS: Record<string, React.ComponentType> = {
    contouring: ContouringStepTools,
    review: ContouringStepTools,
    registration: RegistrationTools,
    'predictive-planning': DoseTools,
};

/**
 * The tools of the current workflow step, laid out as one horizontal row.
 * Sizes to its content, so a host places it in flow. Renders nothing off a
 * workflow step, where there is no viewport to act on. The row is one
 * {@link ToolTrayPresenceProvider} scope: while a tool tray hangs from it, its
 * buttons show no tooltip.
 *
 * Below the row hangs the operation preview statement.
 */
export default function Toolbar() {
    const { currentStep } = useWorkflowSteps();

    if (!currentStep) {
        return null;
    }

    const StepTools = STEP_TOOLS[currentStep.id] ?? DefaultTools;

    return (
        <Box
            data-toolbar-id="step-toolbar"
            sx={{
                // Positioning parent of the column that hangs from the row's
                // bottom edge.
                position: 'relative',
            }}
        >
            <Box
                sx={{
                    // A hover-only tray floats over the column below, so
                    // the tools stack above it.
                    position: 'relative',
                    zIndex: 1,
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 0.5,
                }}
            >
                <ToolTrayPresenceProvider>
                    <StepTools />
                </ToolTrayPresenceProvider>
            </Box>
            <Box
                sx={{
                    position: 'absolute',
                    top: '100%',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    // Sized to its content, not to the half of the row that
                    // an absolute box starting at the row's centre gets.
                    width: 'max-content',
                    mt: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 0.75,
                    pointerEvents: 'none',
                }}
            >
                <OperationPreviewIndicator />
            </Box>
        </Box>
    );
}
